import React, { HTMLAttributes, MouseEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';

export interface DialogProps extends HTMLAttributes<HTMLDialogElement> {
  open: boolean;
  onClose: () => void;
  closeOnOutsideClick?: boolean;
  children?: React.ReactNode;
  contentClassName?: string;
}

export function Dialog({
  open,
  onClose,
  closeOnOutsideClick = true,
  className,
  contentClassName,
  children,
  ...rest
}: DialogProps) {
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const lastActiveElement = React.useRef<Element | null>(null);

  React.useEffect(() => {
    const dialogNode = dialogRef.current;
    if (open) {
      lastActiveElement.current = document.activeElement;
      dialogNode?.showModal();
      document.body.classList.add('overflow-hidden');
    } else {
      dialogNode?.close();
      (lastActiveElement.current as HTMLElement | null)?.focus?.();
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      dialogNode?.close();
      (lastActiveElement.current as HTMLElement | null)?.focus?.();
      document.body.classList.remove('overflow-hidden');
    };
  }, [open]);

  React.useEffect(() => {
    const dialogNode = dialogRef.current;
    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialogNode?.addEventListener('cancel', handleCancel);
    return () => {
      dialogNode?.removeEventListener('cancel', handleCancel);
    };
  }, [onClose]);

  const handleOutsideClick: MouseEventHandler<HTMLDialogElement> = (event) => {
    if (closeOnOutsideClick && event.target === dialogRef.current) {
      onClose();
    }
  };

  return (
    <dialog {...rest} ref={dialogRef} className={twMerge('p-4', className)} onClick={handleOutsideClick}>
      <div className={twMerge('', contentClassName)}>{children}</div>
    </dialog>
  );
}
